import { useEffect, useState, useRef } from 'react'
import { Trash2, CheckSquare, Square, ArrowLeft, Mail, Clock, Users } from 'lucide-react'
import { api } from '../../api/client'
import type { Message } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

export function MailboxPage() {
  const { t } = useI18n()
  const [messages, setMessages] = useState<Message[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')
  const [viewing, setViewing] = useState<Message | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = () => api.get<Message[]>('/messages?folder=inbox').then((data) => { setMessages(data); setSelected(new Set()) }).catch((e: Error) => setError(e.message))
  useEffect(() => {
    load()
    intervalRef.current = setInterval(load, 15000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [])

  function toggleSelect(id: string) {
    setSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }
  function toggleAll() {
    if (selected.size === messages.length) setSelected(new Set())
    else setSelected(new Set(messages.map((m) => m.id)))
  }

  async function deleteSelected() {
    if (selected.size === 0) return
    if (!confirm(`${t('common.delete')} ${selected.size}?`)) return
    try { await api.post('/messages/batch-delete', { ids: [...selected] }); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  async function deleteOne(id: string) {
    try { await api.delete(`/messages/${id}`); setViewing(null); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  if (viewing) {
    return <>
      <PageHeader title="user.inbox" action={
        <button onClick={() => setViewing(null)} className="flex h-10 items-center gap-2 rounded-md border px-4 text-sm font-medium hover:bg-muted/50">
          <ArrowLeft size={16}/>{t('common.back')}
        </button>
      } />
      <div className="panel overflow-hidden">
        <div className="border-b p-6">
          <h2 className="text-xl font-semibold">{viewing.subject || '(No subject)'}</h2>
          <div className="mt-4 flex flex-col gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail size={14} className="shrink-0" />
              <span className="font-medium text-foreground">{viewing.sender}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users size={14} className="shrink-0" />
              <span>{viewing.recipients?.join(', ')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} className="shrink-0" />
              <span>{new Date(viewing.createdAt).toLocaleString()}</span>
            </div>
          </div>
        </div>
        <div className="p-6">
          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">{viewing.body}</pre>
        </div>
        <div className="border-t px-6 py-4 flex justify-end">
          <button onClick={() => deleteOne(viewing.id)} className="flex items-center gap-2 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90">
            <Trash2 size={14}/>{t('common.delete')}
          </button>
        </div>
      </div>
    </>
  }

  return <>
    <PageHeader title="user.inbox" action={selected.size > 0 ? <button onClick={deleteSelected} className="flex h-10 items-center gap-2 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground"><Trash2 size={16}/>{selected.size}</button> : undefined} />
    {error && <p className="mb-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
    {messages.length === 0 ? <EmptyState title="user.emptyMailbox" detail="user.emptyMailboxDetail" /> : (
      <div className="panel divide-y overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/20">
          <button onClick={toggleAll}>{selected.size === messages.length ? <CheckSquare size={16}/> : <Square size={16}/>}</button>
          <span className="text-xs text-muted-foreground">{messages.length} {t('admin.messages')}</span>
        </div>
        {messages.map((message) => (
          <article key={message.id} className="flex items-start gap-3 p-4 transition-colors hover:bg-muted/30">
            <button onClick={() => toggleSelect(message.id)} className="mt-1 shrink-0">{selected.has(message.id) ? <CheckSquare size={16}/> : <Square size={16}/>}</button>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setViewing(message)}>
              <div className="flex justify-between gap-4">
                <p className="font-medium truncate">{message.sender}</p>
                <time className="text-xs text-muted-foreground shrink-0">{new Date(message.createdAt).toLocaleString()}</time>
              </div>
              <p className="mt-1 text-sm">{message.subject || '(No subject)'}</p>
              <p className="mt-1 truncate text-xs text-muted-foreground">{message.body}</p>
            </div>
            <button onClick={() => deleteOne(message.id)} className="shrink-0 rounded border p-1.5 text-destructive hover:bg-destructive/10"><Trash2 size={14}/></button>
          </article>
        ))}
      </div>
    )}
  </>
}
